import classNames from 'classnames';
import {Link, WithRouterProps} from 'react-router';
import React, {useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import appStore from 'client/AppStore';
import ClassroomInvitations from './ClassroomInvitations';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {getClassroomInvitationsQuery} from 'lib/ClassroomInvitationUtil/ClassroomInvitationUtil';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import {Icon, Tab} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {
  getClassroomsQuery,
  getStudentQuery
} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import JoinCodeForm from 'client/Classrooms/StudentDashboard/JoinCodeForm/JoinCodeForm.react';
import {LoadingSpinner} from '@albert-io/atomic';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageSidebarDesktop from './StudentHomepageSidebarDesktop';
import StudentHomepageSidebarMobile from './StudentHomepageSidebarMobile';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';
import usePrevious from 'lib/hooks/usePrevious';

interface TabConfig {
  icon: IconProp;
  label: string;
  to: string;
}

interface RequestConfig {
  beforeRequest: () => void;
  onFulfilled: (value: any) => void;
  onRejected: (reason: any) => void;
  onSettled: () => void;
  query: any;
}

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  const {classroomId} = params;
  const [isStudentLoading, setIsStudentLoading] = useState(true);

  const lastIsMobile = usePrevious(appStore.isMobileView());
  const isMobile = appStore.isMobileView();

  const {
    areInvitationsLoading,
    exceededHardCapLimit,
    invitations,
    setAreClassroomsLoading,
    setAreInvitationsLoading,
    setClassrooms,
    setShouldRefetchResources,
    setInvitations,
    setIsSidebarExpanded,
    setShowJoinClassModal,
    setStudent,
    shouldRefetchResources,
    showJoinClassModal,
    student,
    studentHasInvalidEmail
  } = useStudentDashboard(
    useShallow((state) => ({
      areInvitationsLoading: state.areInvitationsLoading,
      exceededHardCapLimit: state.exceededHardCapLimit,
      invitations: state.invitations,
      setAreClassroomsLoading: state.setAreClassroomsLoading,
      setAreInvitationsLoading: state.setAreInvitationsLoading,
      setClassrooms: state.setClassrooms,
      setShouldRefetchResources: state.setShouldRefetchResources,
      setShowJoinClassModal: state.setShowJoinClassModal,
      setInvitations: state.setInvitations,
      setIsSidebarExpanded: state.setIsSidebarExpanded,
      setStudent: state.setStudent,
      shouldRefetchResources: state.shouldRefetchResources,
      showJoinClassModal: state.showJoinClassModal,
      student: state.student,
      studentHasInvalidEmail: state.studentHasInvalidEmail
    }))
  );

  const requestConfig: RequestConfig[] = useMemo(
    () =>
      student === null
        ? []
        : [
            {
              beforeRequest: () => {
                setAreClassroomsLoading(true);
              },
              onFulfilled: (value) => {
                setClassrooms(value);
              },
              onRejected: (reason) => {
                console.error(reason);
                // TODO: Add error handling for fetching classrooms
              },
              onSettled: () => {
                setAreClassroomsLoading(false);
              },
              query: getClassroomsQuery(student.getId())
            },
            {
              beforeRequest: () => {
                setAreInvitationsLoading(true);
              },
              onFulfilled: (value) => {
                setInvitations(value);
              },
              onRejected: (reason) => {
                console.error(reason);
                // TODO: Add error handling for fetching invitations
              },
              onSettled: () => {
                setAreInvitationsLoading(false);
              },
              query: getClassroomInvitationsQuery(student.getEmail())
            }
          ],
    [setAreClassroomsLoading, setAreInvitationsLoading, setClassrooms, setInvitations, student]
  );

  // Force sidebar to expand/contract when viewport changes to/from mobile
  useEffect(() => {
    if (lastIsMobile !== isMobile) {
      setIsSidebarExpanded(!isMobile);
    }
  }, [isMobile, lastIsMobile, setIsSidebarExpanded]);

  // Handles fetching the student. Other requests are made separately in the effect below.
  useEffect(() => {
    getStudentQuery()
      .getResourcePromise()
      .then((fetchedStudent) => {
        setStudent(fetchedStudent);
      })
      .catch((error) => {
        // TODO: Add error state
        console.error(error);
      })
      .finally(() => {
        setIsStudentLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Makes other auxiliary requests once the student has been loaded.
  useEffect(() => {
    if (student !== null) {
      const queries: any[] = [];

      requestConfig.forEach(({beforeRequest, query}) => {
        beforeRequest();

        queries.push(query);
      });

      if (shouldRefetchResources) {
        queries.forEach((query) => {
          query.invalidateInterest();
        });

        setShouldRefetchResources(false);
      }

      const promises = queries.map((query) => query.getResourcePromise());

      Promise.allSettled(promises).then((results) => {
        results.forEach((result, index) => {
          const {onFulfilled, onRejected, onSettled} = requestConfig[index];

          const {status} = result;

          if (status === 'fulfilled') {
            onFulfilled(result.value);
          } else {
            onRejected(result.reason);
          }

          onSettled();
        });
      });
    }
  }, [requestConfig, setShouldRefetchResources, shouldRefetchResources, student]);

  function getToMaybeWithClassroomId(suffix: string) {
    let to = '/home';

    if (classroomId !== undefined) {
      to = `${to}/class/${classroomId}`;
    }

    to = `${to}/${suffix}`;

    return to;
  }

  const tabConfigs: TabConfig[] = [
    {
      icon: ['fal', 'file-alt'],
      label: 'Assignments',
      to: getToMaybeWithClassroomId('assignments')
    },
    {
      icon: ['fal', 'sparkles'],
      label: 'Skill Path',
      to: getToMaybeWithClassroomId('skill-path')
    },
    {
      icon: ['fal', 'pencil'],
      label: 'Independent Practice',
      to: getToMaybeWithClassroomId('independent-practice')
    }
  ];

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div
        className={classNames('-mt-[60px] min-h-[calc(100vh-60px)] background-subtle', {
          'flex flex-col': isMobile,
          'justify-center': isMobile && isStudentLoading,
          'grid grid-cols-[max-content_repeat(3,minmax(0,1fr))] grid-rows-[repeat(2,min-content)_1fr]':
            !isMobile
        })}
      >
        {isStudentLoading ? (
          <LoadingSpinner
            className={classNames({
              'col-span-4 row-span-3 self-center': !isMobile
            })}
            bottomMarginLevel={0}
            size={5}
          />
        ) : (
          <>
            {!isMobile && <StudentHomepageSidebarDesktop />}
            <StudentHomepageHeader className={classNames({'col-span-full': !isMobile})} />
            {(areInvitationsLoading || invitations.size > 0) && (
              <div className='col-span-3'>
                {areInvitationsLoading ? (
                  <LoadingSpinner bottomMarginLevel={0} size={2} />
                ) : (
                  <ClassroomInvitations
                    className={classNames('mx-x2 mt-x4 mb-x0', {'col-span-3': !isMobile})}
                  />
                )}
              </div>
            )}
            <div
              className={classNames('mx-x2 my-x3 rounded-3xl background-primary', {
                'flex flex-1 flex-col': isMobile,
                'col-span-3': !isMobile,
                'col-start-2': !isMobile && invitations.size > 0,
                'row-span-2': !isMobile && invitations.size === 0
              })}
            >
              <div className={classNames('flex items-start', {'flex-wrap': isMobile})}>
                {tabConfigs.map(({icon, label, to}) => (
                  <Tab
                    as={Link}
                    className='px-x4 py-x3'
                    contentWrapperClassName='flex gap-x1 items-center'
                    isActive={location.pathname.includes(to)}
                    key={to}
                    to={to}
                  >
                    <Icon icon={icon} />
                    {label}
                  </Tab>
                ))}
              </div>
              <div
                className={classNames('flex items-center justify-center', {
                  'flex-1': isMobile,
                  'h-full': !isMobile
                })}
              >
                {children}
              </div>
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinCodeForm.Modal
                handleClose={() => setShowJoinClassModal(false)}
                isNewStudentDashboard
                onEnrollSuccess={() => setShowJoinClassModal(false)}
              />
            )}
          </>
        )}
      </div>
      {isMobile && <StudentHomepageSidebarMobile />}
    </FeatureFlag>
  );
}
